import React, { useState, useRef } from 'react';
import { api } from '../services/api';

const AVAILABLE_VARIABLES = [
  { key: '{{lead_first_name}}', label: 'First Name', group: 'lead' },
  { key: '{{lead_last_name}}', label: 'Last Name', group: 'lead' },
  { key: '{{lead_phone}}', label: 'Phone', group: 'lead' },
  { key: '{{lead_email}}', label: 'Email', group: 'lead' },
  { key: '{{conversation}}', label: 'Conversation', group: 'lead' },
  { key: '{{dealer_name}}', label: 'Dealer Name', group: 'context' },
  { key: '{{vehicle_type}}', label: 'new/used', group: 'context' },
  { key: '{{source_url}}', label: 'Source URL', group: 'context' },
  { key: '{{source_display_name}}', label: 'Source Name', group: 'context' },
  { key: '{{date}}', label: 'Date (ISO)', group: 'context' },
  { key: '{{thread_id}}', label: 'Thread ID', group: 'context' },
  { key: '{{assistant_id}}', label: 'Assistant ID', group: 'context' },
  { key: '{{make}}', label: 'Make (AI)', group: 'vehicle_ai' },
  { key: '{{model}}', label: 'Model (AI)', group: 'vehicle_ai' },
  { key: '{{year}}', label: 'Year (AI)', group: 'vehicle_ai' },
  { key: '{{trim}}', label: 'Trim (AI)', group: 'vehicle_ai' },
  { key: '{{vin}}', label: 'VIN', group: 'vehicle' },
  { key: '{{stock}}', label: 'Stock #', group: 'vehicle' },
];

const AI_MODELS = [
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
];

const ADFXmlEditor = ({ xmlTemplate, onChange, readOnly = false }) => {
  const textareaRef = useRef(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiModel, setAiModel] = useState('gemini-2.5-flash');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [showVariables, setShowVariables] = useState(false);
  // Conversation history for AI context (kept in browser memory only)
  const [aiHistory, setAiHistory] = useState([]);

  const insertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = xmlTemplate.substring(0, start);
    const after = xmlTemplate.substring(end);
    const newValue = before + variable + after;
    onChange(newValue);
    // Restore cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
    }, 0);
  };

  const handleAiEdit = async () => {
    if (!aiInstruction.trim()) return;
    const instruction = aiInstruction.trim();
    setAiLoading(true);
    setAiError('');
    try {
      const res = await api.post('v1/adf/ai-edit', {
        xml_template: xmlTemplate,
        instruction,
        model: aiModel,
        history: aiHistory.length > 0 ? aiHistory : undefined,
      });
      if (res.data.xml_template) {
        // Append this exchange to history so the LLM remembers it next time
        setAiHistory((prev) => [
          ...prev,
          { role: 'user', content: `Current XML template:\n\n${xmlTemplate}\n\nInstruction: ${instruction}` },
          { role: 'assistant', content: res.data.xml_template },
        ]);
        onChange(res.data.xml_template);
        setAiInstruction('');
      }
    } catch (err) {
      setAiError(err.response?.data?.detail || err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiEdit();
    }
  };

  return (
    <div>
      {/* XML Editor */}
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '6px',
        }}>
          <label style={{ fontSize: '13px', fontWeight: 'bold' }}>XML Template</label>
          <button
            onClick={() => setShowVariables(!showVariables)}
            style={{
              padding: '3px 10px', fontSize: '11px', background: showVariables ? '#e8f0fe' : '#f0f0f0',
              border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer',
            }}
          >
            {showVariables ? 'Hide' : 'Show'} Variables
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={xmlTemplate || ''}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            spellCheck={false}
            style={{
              flex: 1,
              minHeight: '340px',
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: '12.5px',
              lineHeight: '1.5',
              padding: '14px',
              background: '#1e1e1e',
              color: '#d4d4d4',
              border: '1px solid #333',
              borderRadius: '6px',
              resize: 'vertical',
              whiteSpace: 'pre',
              overflowX: 'auto',
              tabSize: 2,
            }}
          />

          {/* Variables panel */}
          {showVariables && (
            <div style={{
              width: '200px', flexShrink: 0,
              background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px',
              padding: '10px', maxHeight: '340px', overflowY: 'auto',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                Available Variables
              </div>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
                Click to insert at cursor
              </div>
              {[
                { group: 'lead', title: 'Lead Info' },
                { group: 'context', title: 'Context' },
                { group: 'vehicle_ai', title: 'Vehicle (AI-extracted)' },
                { group: 'vehicle', title: 'Vehicle (other)' },
              ].map(({ group, title }) => {
                const vars = AVAILABLE_VARIABLES.filter((v) => v.group === group);
                if (vars.length === 0) return null;
                return (
                  <div key={group} style={{ marginBottom: '8px' }}>
                    <div style={{
                      fontSize: '10px', fontWeight: 'bold', color: group === 'vehicle_ai' ? '#7c3aed' : '#555',
                      marginBottom: '3px', textTransform: 'uppercase',
                    }}>
                      {title}
                    </div>
                    {vars.map((v) => (
                      <button
                        key={v.key}
                        onClick={() => insertVariable(v.key)}
                        title={v.label}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '4px 6px', marginBottom: '2px',
                          fontSize: '11px', fontFamily: 'monospace',
                          background: group === 'vehicle_ai' ? '#f5f0ff' : 'white',
                          border: `1px solid ${group === 'vehicle_ai' ? '#d8ccf0' : '#e0e0e0'}`,
                          borderRadius: '3px', cursor: 'pointer',
                          color: group === 'vehicle_ai' ? '#7c3aed' : '#0066cc',
                        }}
                      >
                        {v.key}
                        <span style={{ color: '#888', fontFamily: 'sans-serif', fontSize: '10px', marginLeft: '4px' }}>
                          {v.label}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* AI Input */}
      {!readOnly && (
        <div style={{
          marginTop: '10px', padding: '10px', background: '#f0f4ff',
          borderRadius: '6px', border: '1px solid #c8d6f0',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px',
          }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>AI Edit</span>
            {aiHistory.length > 0 && (
              <span style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ({aiHistory.length / 2} msg{aiHistory.length > 2 ? 's' : ''} in memory)
                <button
                  onClick={() => setAiHistory([])}
                  style={{
                    padding: '1px 5px', fontSize: '10px', color: '#888',
                    background: 'transparent', border: '1px solid #ccc', borderRadius: '3px',
                    cursor: 'pointer',
                  }}
                  title="Clear conversation memory"
                >
                  clear
                </button>
              </span>
            )}
            <select
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value)}
              style={{
                padding: '2px 6px', fontSize: '11px', borderRadius: '3px',
                border: '1px solid #ccc', background: 'white',
              }}
            >
              {AI_MODELS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              type="text"
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
              onKeyDown={handleAiKeyDown}
              placeholder='Ex: "Ajouter une balise source avec le nom Webchat", "Retirer la section provider"...'
              disabled={aiLoading}
              style={{
                flex: 1, padding: '8px 10px', fontSize: '13px',
                border: '1px solid #ccc', borderRadius: '4px',
              }}
            />
            <button
              onClick={handleAiEdit}
              disabled={aiLoading || !aiInstruction.trim()}
              style={{
                padding: '8px 16px', fontSize: '13px', whiteSpace: 'nowrap',
                background: aiLoading ? '#ccc' : '#4a6cf7', color: 'white',
                border: 'none', borderRadius: '4px',
                cursor: aiLoading ? 'default' : 'pointer',
              }}
            >
              {aiLoading ? 'Editing...' : 'Apply'}
            </button>
          </div>
          {aiError && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#c00' }}>
              {aiError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ADFXmlEditor;
